// ── UserManagementPage ──
// Admin view of all users: list, add, edit access flags, and reset local passwords.
// Microsoft (Entra) users have no local password; local users can be reset to a random temp one.
import { useState, useEffect, useRef } from "react";
import { B, ROLES } from "../../../shared/theme";
import { Btn, Card, ErrorMsg, FInput, FSelect, Modal, RoleBadge, SectionHeader, Spinner, Td, Th } from "../../../shared/ui";
import { useApp } from "../../../shared/appContext";

// Administrator is governed by the Role (Admin), not by a flag — so it is not listed here.
const FLAGS = [
  { key: "isportfoliomanager", label: "Portfolio Manager" },
  { key: "isunderwriter", label: "Underwriter" },
  { key: "isbdo", label: "BDO" },
  { key: "isbdomanager", label: "BDO Manager" },
  { key: "istermfunding", label: "Term Funding" },
  { key: "isdrawfunding", label: "Draw Funding" },
  { key: "isaccountmanager", label: "Account Manager" },
  { key: "isbankcredviewer", label: "Bank Cred Viewer" },
  { key: "isservicer", label: "Servicer" },
];

const badge = (bg, fg, label) => <span style={{ fontSize: 12, background: bg, color: fg, padding: "2px 8px", borderRadius: 4, fontWeight: 600, whiteSpace: "nowrap" }}>{label}</span>;

// Reusable checkbox grid for the access flags (used by both Add and Edit modals).
const FlagGrid = ({ values, onToggle }) => (
  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "8px 20px", margin: "6px 0 16px" }}>
    {FLAGS.map(fl => (
      <label key={fl.key} style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13, cursor: "pointer" }}>
        <input type="checkbox" checked={!!values[fl.key]} onChange={e => onToggle(fl.key, e.target.checked)} />
        {fl.label}
      </label>
    ))}
  </div>
);

// ── Approved networks ────────────────────────────────────────────────────────
// The office addresses a user set to "Approved networks only" may use ACE from
// (api/getApprovedNetworks, api/saveApprovedNetwork). One address or a range
// (CIDR). "Add this address" fills in where you are now. Administrators are
// never restricted; the app setting NETWORK_LOCK=off switches the lock off.
const ApprovedNetworksCard = ({ onChanged }) => {
  const { api } = useApp();
  const [d, setD] = useState(null);
  const [err, setErr] = useState(null);
  const [form, setForm] = useState(null);       // { label, cidr, notes }
  const [busy, setBusy] = useState(false);
  const load = () => api.get("getApprovedNetworks").then(x => { setD(x); setErr(null); }).catch(e => setErr(e.message));
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const act = async body => {
    setBusy(true); setErr(null);
    try { await api.post("saveApprovedNetwork", body); setForm(null); await load(); if (onChanged) onChanged(); }
    catch (e) { setErr(e.message); }
    setBusy(false);
  };
  const nets = (d && d.networks) || [];
  const restricted = (d && d.restricted) || [];
  return <Card style={{ marginBottom: 16 }}>
    <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 8, flexWrap: "wrap" }}>
      <div style={{ fontSize: 13, fontWeight: 600 }}>Approved networks</div>
      <div style={{ fontSize: 12, color: "var(--color-text-secondary)" }}>
        Where users set to <strong>Approved networks only</strong> may sign in from — checked at sign-in and on every request.
        Administrators are never restricted.
      </div>
      <span style={{ marginLeft: "auto" }}><Btn variant="primary" disabled={busy} onClick={() => setForm({ label: "", cidr: "", notes: "" })}>+ Add network</Btn></span>
    </div>
    {err && <div style={{ color: "#993C1D", fontSize: 12, marginBottom: 8 }}>{err}</div>}
    {d && <div style={{ fontSize: 12, marginBottom: 10, padding: "8px 12px", borderRadius: 6, background: "var(--color-background-secondary)" }}>
      You are connecting from <strong style={{ fontFamily: "var(--font-mono)" }}>{d.yourIp || "an address the server can't see"}</strong>
      {d.yourIp && (d.yourIpApproved ? <span style={{ color: "#0F6E56" }}> — an approved network.</span>
        : <> — not on the list. <a href="#" onClick={e => { e.preventDefault(); setForm({ label: "", cidr: d.yourIp, notes: "" }); }} style={{ color: B.cyan }}>Add this address</a></>)}
      {!d.lockOn && <div style={{ color: "#7A5200", marginTop: 4 }}>The office lock is switched off (app setting NETWORK_LOCK = off): nobody is being restricted.</div>}
      {restricted.length > 0 && <div style={{ marginTop: 4, color: "var(--color-text-secondary)" }}>
        Restricted to these networks: {restricted.map(r => r.name).join(", ")}.</div>}
      {d.ipHeaders && <div style={{ marginTop: 4, color: "var(--color-text-secondary)", fontSize: 11 }} title="The address headers this request arrived with">
        Read from: {d.ipHeaderPinned ? <>the <code>{d.ipHeaderPinned}</code> header (CLIENT_IP_HEADER)</> : "the platform's address headers"} · <code>{d.ipHeaders}</code></div>}
    </div>}
    {form && <div style={{ display: "grid", gridTemplateColumns: "1.2fr 1fr 1.6fr auto", gap: "0 12px", alignItems: "end", marginBottom: 12 }}>
      <div><div style={lbl}>Name</div><FInput value={form.label} onChange={v => setForm({ ...form, label: v })} placeholder="Chicago office" /></div>
      <div><div style={lbl}>Address or range</div><FInput value={form.cidr} onChange={v => setForm({ ...form, cidr: v })} placeholder="203.0.113.7 or 203.0.113.0/24" /></div>
      <div><div style={lbl}>Notes</div><FInput value={form.notes} onChange={v => setForm({ ...form, notes: v })} placeholder="ISP, circuit, VPN…" /></div>
      <div style={{ display: "flex", gap: 6 }}>
        <Btn variant="primary" disabled={busy || !form.label || !form.cidr} onClick={() => act({ action: "add", ...form })}>Save</Btn>
        <Btn disabled={busy} onClick={() => setForm(null)}>Cancel</Btn>
      </div>
    </div>}
    {!nets.length ? <div style={{ fontSize: 12.5, color: "var(--color-text-secondary)" }}>None yet. Add the office's address before restricting anyone.</div> :
      <table style={{ width: "100%", borderCollapse: "collapse" }}>
        <thead><tr><Th>Name</Th><Th>Address or range</Th><Th>Notes</Th><Th>Added</Th><Th>Status</Th><Th></Th></tr></thead>
        <tbody>{nets.map(n => <tr key={n.id} style={{ opacity: n.isactive ? 1 : 0.55 }}>
          <Td style={{ fontWeight: 500 }}>{n.label}</Td>
          <Td style={{ fontFamily: "var(--font-mono)", fontSize: 12 }}>{n.cidr}</Td>
          <Td style={{ fontSize: 12 }}>{n.notes || "—"}</Td>
          <Td style={{ fontSize: 12 }}>{n.createdby || "—"}{n.createdat ? " · " + String(n.createdat).slice(0, 10) : ""}</Td>
          <Td>{n.isactive ? badge("#E1F5EE", "#0F6E56", "On") : badge("#F1EFE8", "#888780", "Off")}</Td>
          <Td><div style={{ display: "flex", gap: 6, justifyContent: "flex-end" }}>
            <Btn disabled={busy} onClick={() => act({ action: "update", id: n.id, isactive: !n.isactive })}>{n.isactive ? "Turn off" : "Turn on"}</Btn>
            <Btn variant="danger" disabled={busy} onClick={() => { if (window.confirm(`Delete ${n.label} (${n.cidr})?`)) act({ action: "delete", id: n.id }); }}>Delete</Btn>
          </div></Td>
        </tr>)}</tbody>
      </table>}
  </Card>;
};

export const UserManagementPage = () => {
  const { api, audit } = useApp();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [busyId, setBusyId] = useState(null);
  const [reset, setReset] = useState(null);         // { user, tempPassword }
  const [confirmUser, setConfirmUser] = useState(null);
  const [activeUser, setActiveUser] = useState(null);   // user whose Active state is being flipped
  const [editUser, setEditUser] = useState(null);   // existing user whose access is being edited
  const [flagEdits, setFlagEdits] = useState({});
  const [roleEdit, setRoleEdit] = useState("Viewer");
  const [scopeEdit, setScopeEdit] = useState("anywhere");   // where they may sign in from
  const [adding, setAdding] = useState(null);       // new-user draft or null
  const [saving, setSaving] = useState(false);
  const [copied, setCopied] = useState(false);
  const pwRef = useRef(null);

  const load = () => {
    setLoading(true);
    api.get("getUsersList")
      .then(d => { setUsers(d.users || []); setLoading(false); })
      .catch(e => { setErr(e.message); setLoading(false); });
  };
  useEffect(() => { load(); }, []);

  const doReset = async (u) => {
    setConfirmUser(null);
    setBusyId(u.Id);
    try {
      const r = await api.post("resetUserPassword", { userId: u.Id });
      setReset({ user: u, tempPassword: r.tempPassword });
      setCopied(false);
      try { await audit("Reset password", "Users", u.Id, "Reset password for " + u.FullName); } catch (e) {}
    } catch (e) { setErr(e.message); }
    finally { setBusyId(null); }
  };

  // Deactivating is an immediate lockout, not a soft archive: getCurrentUser
  // only resolves users with IsActive = 1, so their very next request 401s and
  // the SPA drops them at the sign-in screen. Nothing they created is touched.
  const setActive = async (u, next) => {
    setActiveUser(null);
    setBusyId(u.Id);
    try {
      await api.post("saveUserFlags", { userId: u.Id, isActive: next });
      try {
        await audit(next ? "Reactivated user" : "Deactivated user", "Users", u.Id,
          (next ? "Reactivated " : "Deactivated ") + u.FullName);
      } catch (e) {}
      load();
    } catch (e) { setErr(e.message); }
    finally { setBusyId(null); }
  };

  const openEdit = (u) => {
    const init = {};
    FLAGS.forEach(fl => { init[fl.key] = !!u[fl.key]; });
    setFlagEdits(init);
    setRoleEdit(ROLES[u.Role] ? u.Role : "Viewer");
    setScopeEdit(u.accessscope || "anywhere");
    setErr(null);
    setEditUser(u);
  };

  const saveFlags = async () => {
    setSaving(true);
    try {
      await api.post("saveUserFlags", { userId: editUser.Id, role: roleEdit, flags: flagEdits,
        // Only once the column exists (sql/2026-10-27_sign_in_security.sql).
        ...(editUser.accessscope !== undefined ? { accessscope: scopeEdit } : {}) });
      try { await audit("Edited access", "Users", editUser.Id, "Updated access for " + editUser.FullName); } catch (e) {}
      setEditUser(null);
      setErr(null);
      load();
    } catch (e) { setErr(e.message); }
    finally { setSaving(false); }
  };

  const openAdd = () => setAdding({ fullName: "", email: "", username: "", role: "Viewer", authType: "local", isActive: true, flags: {} });

  const saveNew = async () => {
    setSaving(true);
    try {
      const r = await api.post("createUser", {
        fullName: adding.fullName, email: adding.email, username: adding.username,
        role: adding.role, authType: adding.authType, isActive: adding.isActive, flags: adding.flags
      });
      try { await audit("Created user", "Users", r.id, "Created user " + adding.fullName); } catch (e) {}
      const created = adding;
      setAdding(null);
      load();
      if (r.tempPassword) { setReset({ user: { FullName: created.fullName }, tempPassword: r.tempPassword }); setCopied(false); }
    } catch (e) { setErr(e.message); }
    finally { setSaving(false); }
  };

  const copyPw = () => {
    if (pwRef.current) {
      pwRef.current.select();
      try { document.execCommand("copy"); setCopied(true); } catch (e) {}
    }
  };

  if (loading) return <Spinner />;
  if (err && !users.length) return <ErrorMsg msg={err} onRetry={load} />;

  return <>
    <SectionHeader title="User management" actions={[
      <Btn key="add" variant="primary" onClick={openAdd}>+ Add User</Btn>
    ]} />

    {err && <div style={{ color: "#993C1D", fontSize: 12, marginBottom: 10 }}>{err}</div>}

    {/* Role reference */}
    <Card style={{ marginBottom: 16 }}>
      <table style={{ width: "100%", borderCollapse: "collapse" }}>
        <thead><tr><Th>Role</Th><Th>Permissions</Th></tr></thead>
        <tbody>{Object.entries(ROLES).map(([name, r]) => (
          <tr key={name}
            onMouseEnter={e => e.currentTarget.style.background = "var(--color-background-secondary)"}
            onMouseLeave={e => e.currentTarget.style.background = ""}>
            <Td><RoleBadge role={name} /></Td>
            <Td><div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
              {r.canEdit && badge("#E1F5EE", "#0F6E56", "Edit")}
              {r.canDelete && badge("#FAECE7", "#993C1D", "Delete")}
              {r.canViewAudit && badge("#E6F1FB", "#185FA5", "Audit log")}
              {r.canManageUsers && badge("#EEEDFE", "#3C3489", "Manage users")}
            </div></Td>
          </tr>
        ))}</tbody>
      </table>
    </Card>

    <Card style={{ marginBottom: 16, overflowX: "auto" }}>
      <table style={{ width: "100%", borderCollapse: "collapse", whiteSpace: "nowrap" }}>
        <thead><tr>
          <Th>Name</Th><Th>Email</Th><Th>Username</Th><Th>Role</Th><Th>Access</Th><Th>Sign-in</Th><Th>Status</Th><Th></Th>
        </tr></thead>
        <tbody>
          {users.map(u => {
            const isEntra = u.authType === "entra";
            const active = FLAGS.filter(fl => u[fl.key]);
            return (
              <tr key={u.Id}
                style={{ opacity: u.IsActive ? 1 : 0.55 }}
                onMouseEnter={e => e.currentTarget.style.background = "var(--color-background-secondary)"}
                onMouseLeave={e => e.currentTarget.style.background = ""}>
                <Td style={{ fontWeight: 500 }}>{u.FullName || "—"}</Td>
                <Td>{u.Email || "—"}</Td>
                <Td>{u.Username || "—"}</Td>
                <Td>{ROLES[u.Role] ? <RoleBadge role={u.Role} /> : (u.Role || "—")}</Td>
                <Td><div style={{ display: "flex", gap: 4, flexWrap: "wrap", maxWidth: 260 }}>
                  {active.length ? active.map(fl => badge("#EAF7FB", B.cyanDark, fl.label)) : <span style={{ fontSize: 12, color: "var(--color-text-secondary)" }}>—</span>}
                </div></Td>
                <Td><div style={{ display: "flex", gap: 4 }}>
                  {isEntra ? badge("#E6F1FB", "#185FA5", "⊞ Microsoft") : badge("#EAF7FB", B.cyanDark, "Local")}
                  {u.accessscope === "approved" && u.Role !== "Admin" && badge("#FDF0D5", "#7A5200", "Office only")}
                </div></Td>
                <Td>{u.IsActive ? badge("#E1F5EE", "#0F6E56", "Active") : badge("#F1EFE8", "#888780", "Inactive")}</Td>
                <Td><div style={{ display: "flex", gap: 6 }}>
                  <Btn onClick={() => openEdit(u)}>Access</Btn>
                  {!isEntra && <Btn disabled={busyId === u.Id} onClick={() => setConfirmUser(u)}>{busyId === u.Id ? "Resetting…" : "Reset Password"}</Btn>}
                  {/* Reactivating is harmless, so it goes straight through.
                      Deactivating locks someone out mid-session, so it asks. */}
                  {u.IsActive
                    ? <Btn variant="danger" disabled={busyId === u.Id} onClick={() => setActiveUser(u)}>
                        {busyId === u.Id ? "…" : "Deactivate"}
                      </Btn>
                    : <Btn variant="primary" disabled={busyId === u.Id} onClick={() => setActive(u, true)}>
                        {busyId === u.Id ? "…" : "Reactivate"}
                      </Btn>}
                </div></Td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </Card>

    <ApprovedNetworksCard onChanged={load} />

    <div style={{ fontSize: 12, color: "var(--color-text-secondary)", marginBottom: 24 }}>
      Microsoft (Entra) users sign in with their company account — there is no local password to reset. Access flags apply to all users regardless of sign-in type.
    </div>

    {/* Add user */}
    {adding && (
      <Modal title="Add user" onClose={() => setAdding(null)} wide>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "12px 16px" }}>
          <div><div style={lbl}>Full Name</div><FInput value={adding.fullName} onChange={v => setAdding({ ...adding, fullName: v })} /></div>
          <div><div style={lbl}>Email</div><FInput value={adding.email} onChange={v => setAdding({ ...adding, email: v })} /></div>
          <div><div style={lbl}>Sign-in</div>
            <FSelect value={adding.authType} onChange={v => setAdding({ ...adding, authType: v })}
              options={[{ value: "local", label: "Local (username / password)" }, { value: "entra", label: "Microsoft (Entra)" }]} /></div>
          <div><div style={lbl}>Role</div>
            <FSelect value={adding.role} onChange={v => setAdding({ ...adding, role: v })} options={Object.keys(ROLES)} /></div>
          {adding.authType === "local" && (
            <div><div style={lbl}>Username</div><FInput value={adding.username} onChange={v => setAdding({ ...adding, username: v })} /></div>
          )}
          <div style={{ alignSelf: "end" }}>
            <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13, cursor: "pointer" }}>
              <input type="checkbox" checked={adding.isActive} onChange={e => setAdding({ ...adding, isActive: e.target.checked })} /> Active
            </label>
          </div>
        </div>
        <div style={{ ...lbl, marginTop: 16 }}>Access</div>
        <FlagGrid values={adding.flags} onToggle={(k, val) => setAdding({ ...adding, flags: { ...adding.flags, [k]: val } })} />
        {adding.authType === "local" && (
          <div style={{ fontSize: 12, color: "var(--color-text-secondary)", marginBottom: 12 }}>
            A temporary password will be generated and shown once after you save.
          </div>
        )}
        <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
          <Btn onClick={() => setAdding(null)}>Cancel</Btn>
          <Btn variant="primary" disabled={saving || !adding.fullName || !adding.email || (adding.authType === "local" && !adding.username)} onClick={saveNew}>{saving ? "Saving…" : "Create User"}</Btn>
        </div>
      </Modal>
    )}

    {/* Edit access flags */}
    {editUser && (
      <Modal title={"Access — " + editUser.FullName} onClose={() => setEditUser(null)}>
        <div style={{ ...lbl, marginBottom: 4 }}>Role</div>
        <div style={{ maxWidth: 260, marginBottom: 6 }}>
          <FSelect value={roleEdit} onChange={setRoleEdit} options={Object.keys(ROLES)} />
        </div>
        <div style={{ fontSize: 11, color: "var(--color-text-secondary)", marginBottom: 14 }}>
          Admin = full access incl. delete &amp; user management · Standard User = edit &amp; audit · Viewer = read-only.
        </div>
        <div style={{ ...lbl, marginBottom: 4 }}>Designations</div>
        <FlagGrid values={flagEdits} onToggle={(k, val) => setFlagEdits({ ...flagEdits, [k]: val })} />
        {editUser.accessscope !== undefined && <>
          <div style={{ ...lbl, marginBottom: 4 }}>Can sign in from</div>
          <div style={{ display: "flex", flexDirection: "column", gap: 6, margin: "4px 0 6px" }}>
            {[["anywhere", "Anywhere"], ["approved", "Approved networks only (the office)"]].map(([v, l]) =>
              <label key={v} style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13, cursor: roleEdit === "Admin" ? "default" : "pointer" }}>
                <input type="radio" name="scope" checked={scopeEdit === v} disabled={roleEdit === "Admin"} onChange={() => setScopeEdit(v)} /> {l}
              </label>)}
          </div>
          <div style={{ fontSize: 11, color: "var(--color-text-secondary)", marginBottom: 14 }}>
            {roleEdit === "Admin"
              ? "Administrators are never restricted, so one office address change can't lock everyone out."
              : "Approved networks only: refused at sign-in and on every page anywhere else — home, travel, phone — until moved back to Anywhere. Networks are listed under Approved networks below."}
          </div>
        </>}
        {/* A refused save stays open and says why, here — not only at the top of the page behind the window. */}
        {err && <div style={{ color: "#993C1D", fontSize: 12, background: "#FBE4DD", borderRadius: 6, padding: "8px 10px", marginBottom: 10 }}>{err}</div>}
        <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
          <Btn onClick={() => setEditUser(null)}>Cancel</Btn>
          <Btn variant="primary" disabled={saving} onClick={saveFlags}>{saving ? "Saving…" : "Save"}</Btn>
        </div>
      </Modal>
    )}

    {/* Confirm deactivate — spells out what actually happens, because "make
        inactive" sounds reversible-and-gentle and this is an instant lockout. */}
    {activeUser && (
      <Modal title={"Deactivate " + activeUser.FullName + "?"} onClose={() => setActiveUser(null)}>
        <div style={{ fontSize: 13, lineHeight: 1.6, marginBottom: 14 }}>
          <strong>{activeUser.FullName}</strong> ({activeUser.Email}) will be signed out on their next
          request and won't be able to sign back in.
        </div>
        <ul style={{ fontSize: 13, lineHeight: 1.6, margin: "0 0 16px", paddingLeft: 20, color: "var(--color-text-secondary)" }}>
          <li>Their accounts, leads, opportunities and notes are <strong>kept</strong> and stay assigned to them.</li>
          <li>They disappear from owner dropdowns and the "test as user" picker.</li>
          <li>Reactivating restores access with the same role and flags — nothing is lost.</li>
          {activeUser.authType === "entra" && (
            <li>This does not disable their Microsoft account — only their access to ACE.</li>
          )}
        </ul>
        <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
          <Btn onClick={() => setActiveUser(null)}>Cancel</Btn>
          <Btn variant="danger" onClick={() => setActive(activeUser, false)}>Deactivate</Btn>
        </div>
      </Modal>
    )}

    {/* Confirm reset */}
    {confirmUser && (
      <Modal title="Reset password?" onClose={() => setConfirmUser(null)}>
        <div style={{ fontSize: 13, lineHeight: 1.5, marginBottom: 16 }}>
          Generate a new temporary password for <strong>{confirmUser.FullName}</strong> ({confirmUser.Email})?
          Their current password will stop working immediately.
        </div>
        <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
          <Btn onClick={() => setConfirmUser(null)}>Cancel</Btn>
          <Btn variant="primary" onClick={() => doReset(confirmUser)}>Reset Password</Btn>
        </div>
      </Modal>
    )}

    {/* Temp password (shown once) — used by both reset and new local user */}
    {reset && (
      <Modal title="Temporary password" onClose={() => setReset(null)}>
        <div style={{ fontSize: 13, lineHeight: 1.5, marginBottom: 12 }}>
          Temporary password for <strong>{reset.user.FullName}</strong>. This is shown <strong>once</strong> — copy it now and give it to the user. They should change it after signing in.
        </div>
        <div style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 16 }}>
          <input ref={pwRef} readOnly value={reset.tempPassword}
            style={{ flex: 1, fontFamily: "monospace", fontSize: 15, letterSpacing: "0.5px", padding: "10px 12px", borderRadius: 6, border: "1px solid " + B.cyan, background: "#EAF7FB", color: "#1a1a1a" }} />
          <Btn variant="primary" onClick={copyPw}>{copied ? "Copied ✓" : "Copy"}</Btn>
        </div>
        <div style={{ display: "flex", justifyContent: "flex-end" }}>
          <Btn onClick={() => setReset(null)}>Done</Btn>
        </div>
      </Modal>
    )}
  </>;
};

const lbl = { fontSize: 11, fontWeight: 600, color: "var(--color-text-secondary)", textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 4 };
